import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

/** RevenueCat's `event.id` and `app_user_id` — both `varchar(255)` columns. */
export const BILLING_EVENT_EXTERNAL_ID_MAX_LENGTH = 255;
export const BILLING_EVENT_APP_USER_ID_MAX_LENGTH = 255;
export const BILLING_EVENT_TYPE_MAX_LENGTH = 80;
/** The column's own limit. A longer explanation is truncated, never lost. */
export const BILLING_EVENT_ERROR_MAX_LENGTH = 500;

/**
 * One recorded store delivery, as staff read it.
 *
 * ## Why this is a surface at all
 *
 * The webhook has written this trail since P23a2 and the only way to read it was
 * `psql` — which is backwards for the question it answers. "This member paid and
 * has no access, why?" is asked by support, not by whoever has a database shell.
 *
 * ## `rawPayload` is NOT here, and that is the whole permission split
 *
 * The list row is the summary: what arrived, for whom, whether it landed, and why
 * not. The payload is the vendor's arbitrary JSON — store identifiers, prices,
 * possibly an email — and it lives on {@link BillingEventDetailSchema} behind a
 * higher permission, with the read audited. A list that carried it would put a
 * third party's raw record of a member into every support operator's browser on
 * every page load.
 */
export const BillingEventSchema = z
  .object({
    id: UuidSchema,
    /** The vendor's own id — what an operator pastes into RevenueCat. */
    externalEventId: z.string().max(BILLING_EVENT_EXTERNAL_ID_MAX_LENGTH),
    eventType: z.string().max(BILLING_EVENT_TYPE_MAX_LENGTH),
    /**
     * This platform's user id **as the purchase SDK was told it** — a string, not
     * a `UuidSchema`, and deliberately so. The column carries no foreign key
     * because an event naming an account that does not exist is a fact worth
     * recording, and a uuid-shaped contract could not carry back the malformed
     * id that is exactly the row support is hunting for.
     */
    appUserId: z.string().max(BILLING_EVENT_APP_USER_ID_MAX_LENGTH).nullable(),
    /**
     * Stamped only when the effect was **applied**. A recorded failure leaves it
     * null (`markFailed` sets it back), so `null` covers two different states and
     * `error` is what separates them — see the outcome the admin surface derives.
     */
    processedAt: z.iso.datetime().nullable(),
    /** Operator-facing English. Never member-visible, never translated. */
    error: z.string().max(BILLING_EVENT_ERROR_MAX_LENGTH).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type BillingEvent = z.infer<typeof BillingEventSchema>;

/**
 * One delivery **with** the vendor's payload — the dispute-answering read.
 *
 * `z.unknown()` is U4's sanctioned untyped boundary rather than laziness. This is
 * a third party's JSON whose shape they change without telling us: the webhook's
 * own contract already declines to `.strict()` it for that reason (P23a2 — "the
 * next field they add" must not become a lost purchase), and giving it a shape
 * here would be a promise the next RevenueCat release breaks. What the operator
 * needs is *what the store actually said*, verbatim.
 */
export const BillingEventDetailSchema = BillingEventSchema.extend({
  rawPayload: z.unknown(),
}).strict();

export type BillingEventDetail = z.infer<typeof BillingEventDetailSchema>;
